/**
 * The navigation decision, as a **pure function** (§9.1's guard rules, V17's session model).
 *
 * It lives apart from `router/index.ts` for one reason: D57 adds no frontend test framework in
 * v1, so the only thing that can be asserted about the guard is what a plain node script can
 * import and call. A decision that depends on nothing — no store, no router, no window — is
 * exactly that, and `scripts/check-guard-decision.mjs` (`yarn check:guard`) walks a table of
 * cases through it, including the regression this file was extracted for:
 *
 *   **an unsettled session is never treated as signed in.** A visitor whose `/auth/me` has not
 *   answered yet is anonymous as far as routing is concerned, so they end at /login carrying
 *   `?redirect=`, never on the app shell with no data in it.
 *
 * Deliberately free of runtime imports (types only): the check script loads this module directly
 * with node's type stripping, and an `@/…` import would drag the whole SPA in behind it.
 */

/** Route names this decision knows by name. Spelled once so the guard and the router agree. */
export const LoginRoute     = 'login'
export const DashboardRoute = 'dashboard'
export const NoAccessRoute  = 'no-access'
export const CallbackRoute  = 'auth-callback'

/** The two `meta` flags the guard reads, lifted out of vue-router's open `RouteMeta`. */
export interface RouteFlags {
    /**
     * `false` marks a route as **public**. Anything else — `true`, or absent — requires a
     * session, which is the fail-safe direction: a route added without a `meta` block is guarded
     * rather than silently open. Every route in `router/index.ts` states the flag explicitly.
     */
    requiresAuth?:        boolean
    /** §9.1's cosmetic gate on the users screens; the server enforces it regardless. */
    requiresManageUsers?: boolean
}

/** The half of `RouteLocationNormalized` this decision reads. */
export interface NavigationTarget {
    name?:    string
    fullPath: string
    meta:     RouteFlags
}

/**
 * The session, as four booleans. `settled` is separate from `loggedIn` on purpose: "nobody is
 * signed in" and "we do not know yet" must not collapse into one flag here, because the second
 * one is the state this whole file exists to route correctly.
 */
export interface SessionSnapshot {
    /** The boot `GET /auth/me` has answered (or failed). False while it is still in flight. */
    settled:        boolean
    loggedIn:       boolean
    hasAnyAccess:   boolean
    canManageUsers: boolean
}

/** What a `beforeEach` may return: proceed, or go somewhere else. */
export type GuardDecision = true | { name: string, query?: { redirect: string } }

/** Public = explicitly opted out of the session requirement. See [RouteFlags.requiresAuth]. */
export const isPublicRoute = (to: NavigationTarget): boolean => to.meta.requiresAuth === false

export const decideNavigation = (to: NavigationTarget, session: SessionSnapshot): GuardDecision => {
    // The callback view owns its own outcome: it replaces to the validated redirect target on
    // success and to /login on failure. Deciding it here would race that decision.
    if (to.name === CallbackRoute) return true

    // An unsettled bootstrap is an anonymous visitor. The router awaits `sessionReady` before
    // calling this, so `settled` is normally true — but the invariant is stated here rather than
    // assumed, because the failure mode it prevents (the app shell rendered for a session nobody
    // has established yet) is precisely what the login race looked like from the outside.
    const loggedIn = session.settled && session.loggedIn

    if (!loggedIn) {
        // Public routes are reachable signed-out; everything else goes back to the start,
        // carrying where it was going so the sign-in lands there rather than on the dashboard.
        return isPublicRoute(to) ? true : { name: LoginRoute, query: { redirect: to.fullPath } }
    }

    if (to.name === LoginRoute) return { name: DashboardRoute }

    // A provisioned user with no role and no assignment holds a perfectly valid session and is
    // 403'd everywhere (§10.2) — so the shell is pointless for them and every request from it
    // would be a rejection. They get exactly one screen until someone grants them something.
    if (!session.hasAnyAccess) {
        return to.name === NoAccessRoute ? true : { name: NoAccessRoute }
    }
    if (to.name === NoAccessRoute) return { name: DashboardRoute }

    // §9.1, §10.4: the users screens are cosmetically gated here the same way the sidebar entry
    // is hidden (`layouts/layout.ts`) — every route behind this is independently enforced by
    // `Logic.requireUserManager` regardless of what the router does, so this exists only to avoid
    // rendering a screen that will 403 on its first request.
    if (to.meta.requiresManageUsers && !session.canManageUsers) return { name: DashboardRoute }

    return true
}
